import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  CreditCard,
  Check,
  Star,
  AlertTriangle,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { useTikTokFetch } from '../hooks/useTikTokFetch';
import '../styles/pages.css';

interface Plan {
  id: string;
  name: string;
  price: number;
  price_display: string;
  trial_days: number;
  features: string[];
  conversion_limit: number;
  creator_limit: number;
  popular: boolean;
}

interface Subscription {
  plan: string;
  status: string;
  features: string[];
  conversion_limit: number;
  creator_limit: number;
  current_period_end: string | null;
}

interface Usage {
  plan: string;
  usage: {
    conversions: {
      used: number;
      limit: number;
      remaining: number;
      percentage: number;
    };
    creators: {
      used: number;
      limit: number;
      remaining: number;
      percentage: number;
    };
  };
}

const PLAN_ICONS: Record<string, typeof Zap> = {
  free: Zap,
  starter: TrendingUp,
  pro: Star,
  enterprise: Users,
};

export default function Billing() {
  const { get, post, getShopId } = useTikTokFetch();
  const shopId = getShopId();
  const queryClient = useQueryClient();

  const [upgradeModal, setUpgradeModal] = useState<Plan | null>(null);
  const [cancelModal, setCancelModal] = useState(false);

  // Fetch plans
  const { data: plansData, isLoading: plansLoading } = useQuery<{ plans: Plan[] }>({
    queryKey: ['billing-plans'],
    queryFn: () => get('/api/billing/plans'),
  });

  // Fetch subscription
  const { data: subscription, isLoading: subLoading } = useQuery<Subscription>({
    queryKey: ['billing-subscription', shopId],
    queryFn: () => get('/api/billing/subscription'),
    enabled: !!shopId,
  });

  // Fetch usage
  const { data: usage, isLoading: usageLoading } = useQuery<Usage>({
    queryKey: ['billing-usage', shopId],
    queryFn: () => get('/api/billing/usage'),
    enabled: !!shopId,
  });

  // Subscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: (planId: string) =>
      post<{ confirmation_url?: string; status?: string }>('/api/billing/subscribe', {
        plan: planId,
      }),
    onSuccess: (data) => {
      if (data?.confirmation_url) {
        window.location.href = data.confirmation_url;
      } else {
        queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
        queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
        setUpgradeModal(null);
      }
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => post('/api/billing/cancel'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      setCancelModal(false);
    },
  });

  const handleConfirmUpgrade = useCallback(() => {
    if (upgradeModal) {
      subscribeMutation.mutate(upgradeModal.id);
    }
  }, [upgradeModal, subscribeMutation]);

  const isLoading = plansLoading || subLoading || usageLoading;
  const plans = plansData?.plans || [];
  const currentPlan = subscription?.plan || 'free';
  const isFreePlan = currentPlan === 'free';

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">Billing</h1>

      {/* Current Plan Overview */}
      <motion.section
        className="card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="card-header">
          <CreditCard size={18} />
          <h2>Current Plan</h2>
        </div>
        <div className="card-body">
          <div className="billing-current">
            <div className="billing-plan-name">
              <span className={`badge ${isFreePlan ? 'badge--warning' : 'badge--success'}`}>
                {plans.find((p) => p.id === currentPlan)?.name || 'Free'}
              </span>
              {!isFreePlan && subscription?.current_period_end && (
                <span className="text-secondary text-sm">
                  Next billing:{' '}
                  {new Date(subscription.current_period_end).toLocaleDateString()}
                </span>
              )}
            </div>
            {isFreePlan && (
              <div className="billing-banner billing-banner--info">
                <Zap size={16} />
                <span>
                  You are on the Free plan. Upgrade to unlock more conversions, creators, and
                  advanced features.
                </span>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Usage Stats */}
      {usage && (
        <motion.section
          className="card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <div className="card-header">
            <TrendingUp size={18} />
            <h2>Usage This Month</h2>
          </div>
          <div className="card-body">
            <div className="usage-grid">
              {/* Conversions */}
              <div className="usage-item">
                <div className="usage-label-row">
                  <span className="text-secondary">Conversions</span>
                  <span className="mono text-sm">
                    {usage.usage.conversions.limit === -1
                      ? `${usage.usage.conversions.used} / Unlimited`
                      : `${usage.usage.conversions.used} / ${usage.usage.conversions.limit}`}
                  </span>
                </div>
                {usage.usage.conversions.limit !== -1 && (
                  <div className="usage-bar">
                    <div
                      className={`usage-bar-fill ${
                        usage.usage.conversions.percentage > 80 ? 'usage-bar-fill--danger' : ''
                      }`}
                      style={{ width: `${Math.min(100, usage.usage.conversions.percentage)}%` }}
                    />
                  </div>
                )}
              </div>
              {/* Creators */}
              <div className="usage-item">
                <div className="usage-label-row">
                  <span className="text-secondary">Active Creators</span>
                  <span className="mono text-sm">
                    {usage.usage.creators.limit === -1
                      ? `${usage.usage.creators.used} / Unlimited`
                      : `${usage.usage.creators.used} / ${usage.usage.creators.limit}`}
                  </span>
                </div>
                {usage.usage.creators.limit !== -1 && (
                  <div className="usage-bar">
                    <div
                      className={`usage-bar-fill ${
                        usage.usage.creators.percentage > 80 ? 'usage-bar-fill--danger' : ''
                      }`}
                      style={{ width: `${Math.min(100, usage.usage.creators.percentage)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Plan Cards */}
      <div className="plans-grid">
        {plans.map((plan, i) => {
          const PlanIcon = PLAN_ICONS[plan.id] || Zap;
          const isCurrent = currentPlan === plan.id;

          return (
            <motion.div
              key={plan.id}
              className={`plan-card ${plan.popular ? 'plan-card--popular' : ''} ${
                isCurrent ? 'plan-card--current' : ''
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
            >
              {plan.popular && (
                <div className="plan-popular-tag">
                  <Star size={12} />
                  Most Popular
                </div>
              )}
              <div className="plan-header">
                <div className="plan-icon">
                  <PlanIcon size={20} />
                </div>
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-price">{plan.price_display}</div>
                {plan.trial_days > 0 && (
                  <span className="text-accent text-sm">{plan.trial_days}-day free trial</span>
                )}
              </div>
              <div className="plan-features">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="plan-feature">
                    <Check size={14} className="text-accent" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <div className="plan-action">
                {isCurrent ? (
                  <button className="btn btn--sm" disabled>
                    Current Plan
                  </button>
                ) : (
                  <button
                    className={`btn ${plan.popular ? 'btn--primary' : 'btn--secondary'}`}
                    onClick={() => setUpgradeModal(plan)}
                  >
                    {plan.price === 0
                      ? 'Downgrade'
                      : plan.price > (plans.find((p) => p.id === currentPlan)?.price || 0)
                      ? 'Upgrade'
                      : 'Switch'}
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Cancel Section */}
      {!isFreePlan && (
        <motion.section
          className="card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="card-header">
            <AlertTriangle size={18} />
            <h2>Cancel Subscription</h2>
          </div>
          <div className="card-body">
            <p className="text-secondary" style={{ marginBottom: '1rem' }}>
              If you cancel, you will be downgraded to the Free plan at the end of your current
              billing period.
            </p>
            <button className="btn btn--danger" onClick={() => setCancelModal(true)}>
              Cancel Subscription
            </button>
          </div>
        </motion.section>
      )}

      {/* Upgrade Modal */}
      {upgradeModal && (
        <div className="modal-overlay" onClick={() => setUpgradeModal(null)}>
          <motion.div
            className="modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>
                {upgradeModal.price === 0 ? 'Downgrade' : 'Upgrade'} to {upgradeModal.name}
              </h3>
            </div>
            <div className="modal-body">
              {upgradeModal.price === 0 ? (
                <div className="billing-banner billing-banner--warning">
                  <AlertTriangle size={16} />
                  <div>
                    <p>Downgrading to the Free plan will:</p>
                    <ul>
                      <li>Reduce conversions to {upgradeModal.conversion_limit}/month</li>
                      <li>Limit creators to {upgradeModal.creator_limit}</li>
                      <li>Remove access to advanced features</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <>
                  <p>
                    You are upgrading to the <strong>{upgradeModal.name}</strong> plan at{' '}
                    <strong>{upgradeModal.price_display}</strong>.
                  </p>
                  {upgradeModal.trial_days > 0 && (
                    <div className="billing-banner billing-banner--success">
                      <Check size={16} />
                      <span>
                        You will get a {upgradeModal.trial_days}-day free trial. No charge until it
                        ends.
                      </span>
                    </div>
                  )}
                </>
              )}
              {subscribeMutation.isError && (
                <div className="billing-banner billing-banner--danger">
                  <AlertTriangle size={16} />
                  <span>Failed to process subscription. Please try again.</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn--secondary" onClick={() => setUpgradeModal(null)}>
                Cancel
              </button>
              <button
                className="btn btn--primary"
                onClick={handleConfirmUpgrade}
                disabled={subscribeMutation.isPending}
              >
                {subscribeMutation.isPending
                  ? 'Processing...'
                  : upgradeModal.price === 0
                  ? 'Confirm Downgrade'
                  : 'Continue to Payment'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <div className="modal-overlay" onClick={() => setCancelModal(false)}>
          <motion.div
            className="modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Cancel Subscription</h3>
            </div>
            <div className="modal-body">
              <div className="billing-banner billing-banner--warning">
                <AlertTriangle size={16} />
                <div>
                  <p>Are you sure? You will lose access to:</p>
                  <ul>
                    {subscription?.features.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  <p>Your plan remains active until the end of the billing period.</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn--secondary" onClick={() => setCancelModal(false)}>
                Keep Subscription
              </button>
              <button
                className="btn btn--danger"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
